import React, { useState, useEffect, useCallback, useRef } from 'react';
import Hero from '../sections/Hero';
import Stats from '../sections/Stats';
import About from '../sections/About';
import Features from '../sections/Features';
import ProblemSolution from '../sections/ProblemSolution';
import Testimonials from '../sections/Testimonials';
import Contact from '../sections/Contact';

// Enhanced AI response system with context awareness
const AI_RESPONSES = {
  GREETINGS: {
    patterns: ['hello', 'hi', 'hey', 'good morning', 'good afternoon', 'good evening'],
    responses: [
      "Hello! I'm Eng. Mathias, your solar energy consultant at DuaAxis. How can I help you today? 🌞",
      "Hi there! Welcome to DuaAxis. I'm here to answer all your solar tracking questions!",
      "Greetings! I'm Eng. Mathias from DuaAxis. Ready to explore how solar tracking can transform your energy production?"
    ]
  },
  PRODUCT_INFO: {
    patterns: ['product', 'tracker', 'system', 'technology', 'how it works', 'features'],
    responses: [
      "Our dual-axis solar trackers increase energy production by up to 45% compared to fixed systems! They automatically follow the sun's path throughout the day for maximum efficiency. ☀️",
      "DuaAxis trackers use advanced AI algorithms to optimize sun positioning in real-time. Our systems feature weather adaptation, remote monitoring, and 25-year durability.",
      "The magic is in our dual-axis design - it tracks both daily east-west movement and seasonal north-south changes. This ensures optimal angle year-round!"
    ]
  },
  PRICING: {
    patterns: ['price', 'cost', 'how much', 'investment', 'affordable'],
    responses: [
      "Our systems start at $15,000 for residential installations, with commercial solutions beginning at $50,000. Would you like a personalized quote? 💰",
      "The investment varies based on your energy needs and location. On average, our customers see ROI within 3-5 years through energy savings!",
      "We offer flexible financing options and can help you explore available solar incentives and tax credits in your area."
    ]
  },
  INSTALLATION: {
    patterns: ['install', 'setup', 'time', 'how long', 'process'],
    responses: [
      "Installation typically takes 2-4 days for residential systems. Our certified team handles everything from site assessment to grid connection! 🛠️",
      "The process includes site evaluation, custom design, installation, and commissioning. We ensure minimal disruption to your operations.",
      "Most commercial installations are completed within 2-3 weeks, depending on system size and site complexity."
    ],
  },
  BENEFITS: {
    patterns: ['benefit', 'advantage', 'why', 'better', 'efficiency'],
    responses: [
      "DuaAxis trackers boost energy production by 35-45%, reduce payback period by 2 years, and increase property value! 📈",
      "Beyond higher efficiency, our systems provide reliable performance in various weather conditions and come with comprehensive monitoring.",
      "You'll enjoy maximum energy harvest, reduced carbon footprint, and long-term energy cost stability with our advanced tracking technology."
    ]
  },
  MAINTENANCE: {
    patterns: ['maintenance', 'service', 'repair', 'warranty', 'support'],
    responses: [
      "Our systems require minimal maintenance - just semi-annual inspections. We offer 10-year comprehensive warranty with 24/7 remote monitoring! 🔧",
      "DuaAxis provides proactive maintenance alerts and has service teams across major regions. Most issues can be resolved remotely through our monitoring system.",
      "We offer tiered service packages from basic monitoring to full maintenance programs. All systems include 5 years of complimentary remote support."
    ]
  },
  CONTACT: {
    patterns: ['contact', 'speak', 'talk', 'meeting', 'consultation'],
    responses: [
      "I'd be happy to connect you with our solar experts! You can call us at +20 2 1234 5678 or schedule a consultation through our website. 📞",
      "Our team is available Monday-Friday 8AM-6PM. Would you like me to schedule a callback with one of our energy consultants?",
      "For immediate assistance, call our support line. For detailed project discussions, I recommend booking a virtual meeting with our engineering team."
    ]
  },
  URGENT: {
    patterns: ['emergency', 'urgent', 'broken', 'not working', 'issue', 'problem'],
    responses: [
      "I understand this is urgent! Let me connect you immediately with our support team. They're available 24/7 for emergency issues. 🚨",
      "For urgent matters, please call our emergency line at +20 2 1234 5678. Our team will provide immediate assistance.",
      "I see this requires urgent attention. Let me escalate this to our technical team right away."
    ]
  },
  COMPARISON: {
    patterns: ['compare', 'vs', 'versus', 'difference', 'better than'],
    responses: [
      "Compared to fixed systems, our trackers produce 35-45% more energy. Compared to single-axis trackers, we deliver 15-25% additional efficiency! 📊",
      "DuaAxis stands out with dual-axis precision, AI optimization, and Egyptian engineering tailored for local conditions.",
      "While single-axis trackers only follow daily movement, our dual-axis systems adjust for both daily and seasonal changes - that's the game-changer!"
    ]
  },
  DEFAULT: {
    responses: [
      "That's an interesting question about solar energy! While I specialize in DuaAxis tracking systems, our human experts would be perfect for detailed discussions. Would you like me to connect you with a solar consultant?",
      "I'm constantly learning about solar technology! For specific technical questions, our engineering team has the deepest expertise. Shall I arrange a consultation?",
      "Great question! Our solar specialists have extensive experience with similar inquiries. I can have them contact you with a comprehensive answer."
    ]
  }
};

const CHAT_STATES = {
  MINIMIZED: 'minimized',
  OPEN: 'open',
  MAXIMIZED: 'maximized'
};

const TYPING_INDICATORS = [
  "Analyzing your question...",
  "Consulting our solar database...",
  "Calculating optimal solutions...",
  "Checking technical specifications...",
  "Reviewing installation scenarios..."
];

const Home = () => {
  const [activeSection, setActiveSection] = useState('hero');
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [chatState, setChatState] = useState(CHAT_STATES.MINIMIZED);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [isOnline, setIsOnline] = useState(true);
  const [systemNotification, setSystemNotification] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [currentTypingText, setCurrentTypingText] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [conversationContext, setConversationContext] = useState([]);
  const [suggestedQuestions, setSuggestedQuestions] = useState([]);
  const [chatRating, setChatRating] = useState(0);
  const [showRating, setShowRating] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [isListening, setIsListening] = useState(false);
  
  const chatContainerRef = useRef(null);
  const messagingTimeoutRef = useRef(null);
  const notificationTimeoutRef = useRef(null);
  const typingIntervalRef = useRef(null);
  const recognitionRef = useRef(null);

  // Enhanced AI with context awareness
  const generateAIResponse = useCallback((userMessage, context = []) => {
    const message = userMessage.toLowerCase();
    let priorityCategory = null;
    
    // Check for urgent matters first
    for (const [category, data] of Object.entries(AI_RESPONSES)) {
      if (category === 'DEFAULT') continue;
      
      const hasMatch = data.patterns.some(pattern => {
        const regex = new RegExp(`\\b${pattern}\\b`, 'i');
        return regex.test(message);
      });
      
      if (hasMatch) {
        if (category === 'URGENT') {
          priorityCategory = category;
          break;
        } else if (!priorityCategory) {
          priorityCategory = category;
        }
      }
    }

    const selectedCategory = priorityCategory || 'DEFAULT';
    const responses = AI_RESPONSES[selectedCategory].responses;
    
    // Add context awareness
    let response = responses[Math.floor(Math.random() * responses.length)];
    
    // Personalize based on location if available
    if (userLocation && response.includes('area')) {
      response = response.replace('your area', `${userLocation.country}`);
    }

    // Add follow-up questions based on context
    const followUps = {
      PRICING: "Would you like me to calculate a personalized estimate for your specific needs?",
      INSTALLATION: "Would you like to schedule a site assessment with our engineering team?",
      PRODUCT_INFO: "Would you like to see a live demo of our tracking technology?",
      CONTACT: "Shall I connect you with one of our solar specialists right now?",
      DEFAULT: "Is there anything else about solar tracking I can help you with today?"
    };

    if (followUps[selectedCategory] && Math.random() > 0.3) {
      response += `\n\n${followUps[selectedCategory]}`;
    }

    return response;
  }, [userLocation]);

  // Enhanced typing simulation with multiple stages
  const simulateTyping = useCallback((userInput, callback) => {
    setIsTyping(true);
    const typingText = TYPING_INDICATORS[Math.floor(Math.random() * TYPING_INDICATORS.length)];
    setCurrentTypingText(typingText);

    // Show typing indicator for 1-2 seconds
    const typingTime = 1000 + Math.random() * 1000;
    
    if (typingIntervalRef.current) clearTimeout(typingIntervalRef.current);
    
    typingIntervalRef.current = setTimeout(() => {
      setIsTyping(false);
      setCurrentTypingText('');
      const aiResponse = generateAIResponse(userInput, conversationContext);
      callback(aiResponse);
    }, typingTime);
  }, [conversationContext, generateAIResponse]);

  // Generate dynamic suggested questions based on conversation
  const generateSuggestedQuestions = useCallback((currentContext) => {
    const baseQuestions = [
      "How much does it cost?",
      "What's the installation process?",
      "Tell me about maintenance",
      "What are the energy savings?",
      "Speak to human expert"
    ];

    const contextAwareQuestions = {
      PRICING: ["Get detailed quote", "Financing options", "ROI calculation"],
      INSTALLATION: ["Site requirements", "Timeline", "Permit assistance"],
      PRODUCT_INFO: ["Technical specs", "Performance data", "Warranty details"],
      MAINTENANCE: ["Service packages", "Emergency support", "Remote monitoring"]
    };

    let suggestions = [...baseQuestions];
    
    // Add context-aware suggestions
    currentContext.forEach(ctx => {
      if (contextAwareQuestions[ctx]) {
        suggestions = [...suggestions, ...contextAwareQuestions[ctx]];
      }
    });

    return [...new Set(suggestions)].slice(0, 5);
  }, []);

  // Enhanced send message with context tracking
  const sendMessage = async () => {
    if (!userInput.trim()) return;

    // Add user message
    const userMessage = {
      id: Date.now(),
      text: userInput,
      sender: 'user',
      timestamp: new Date(),
      context: []
    };

    setChatMessages(prev => [...prev, userMessage]);
    setUserInput('');
    
    // Detect context from user message
    const newContext = [];
    for (const [category, data] of Object.entries(AI_RESPONSES)) {
      if (data.patterns && data.patterns.some(pattern => userInput.toLowerCase().includes(pattern))) {
        newContext.push(category);
      }
    }

    setConversationContext(prev => [...new Set([...prev, ...newContext])]);
    
    // Simulate AI thinking with enhanced typing
    simulateTyping(userInput, (aiResponse) => {
      const aiMessage = {
        id: Date.now() + 1,
        text: aiResponse,
        sender: 'ai',
        timestamp: new Date(),
        context: newContext
      };
      
      setChatMessages(prev => [...prev, aiMessage]);
      
      // Update suggested questions
      const updatedContext = [...conversationContext, ...newContext];
      setSuggestedQuestions(generateSuggestedQuestions(updatedContext));
      
      // Show rating after 3+ message exchanges
      if (chatMessages.length >= 4 && !showRating) {
        setTimeout(() => setShowRating(true), 1000);
      }

      // Add to chat history for context
      const newChatHistory = [...chatHistory, { user: userInput, ai: aiResponse, context: newContext }];
      setChatHistory(newChatHistory);
      try {
        localStorage.setItem('duaaxis-chat-history', JSON.stringify(newChatHistory));
      } catch (error) {
        console.error('Error saving chat history:', error);
      }
    });
  };

  // Handle quick reply selection
  const handleQuickReply = (reply) => {
    setUserInput(reply);
    // Use setTimeout to ensure state is updated before sending
    setTimeout(() => {
      sendMessage();
    }, 100);
  };

  // Auto-expand textarea
  const handleInputChange = (e) => {
    setUserInput(e.target.value);
    
    // Auto-expand textarea
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
  };

  // Voice input functionality
  const startVoiceInput = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      showSystemNotification('Voice input not supported', 'Please use Chrome browser for voice features', 'warning');
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (!recognitionRef.current) {
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'en-US';

      recognitionRef.current.onresult = (event) => {
        const transcript = Array.from(event.results)
          .map(result => result[0])
          .map(result => result.transcript)
          .join('');
        setUserInput(transcript);
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error', event.error);
        setIsListening(false);
        showSystemNotification('Voice input failed', 'Please try again or type your message', 'error');
      };
    }

    try {
      recognitionRef.current.start();
      setIsListening(true);
    } catch (error) {
      console.error('Error starting voice recognition:', error);
      setIsListening(false);
    }
  };

  const stopVoiceInput = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      setIsListening(false);
    }
  };

  // Chat state management functions
  const openChat = () => {
    setChatState(CHAT_STATES.OPEN);
    setUnreadMessages(0);
  };

  const maximizeChat = () => {
    setChatState(CHAT_STATES.MAXIMIZED);
  };

  const minimizeChat = () => {
    setChatState(CHAT_STATES.OPEN);
  };

  const closeChat = () => {
    setChatState(CHAT_STATES.MINIMIZED);
  };

  const toggleChat = () => {
    if (chatState === CHAT_STATES.MINIMIZED) {
      openChat();
    } else {
      closeChat();
    }
  };

  const showSystemNotification = (title, message, type = 'info') => {
    setSystemNotification({ title, message, type, id: Date.now() });
    if (notificationTimeoutRef.current) clearTimeout(notificationTimeoutRef.current);
    notificationTimeoutRef.current = setTimeout(() => {
      setSystemNotification(null);
    }, 5000);
  };

  // Enhanced Message Component
  const Message = ({ message }) => (
    <div className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[80%] rounded-2xl p-4 shadow-sm ${
          message.sender === 'user'
            ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-br-none'
            : 'bg-gray-50 text-gray-800 rounded-bl-none border border-gray-100'
        }`}
      >
        <p className="text-sm whitespace-pre-wrap leading-relaxed">{message.text}</p>
        <p className={`text-xs mt-2 ${
          message.sender === 'user' ? 'text-primary-100' : 'text-gray-500'
        }`}>
          {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </p>
      </div>
    </div>
  );

  // Enhanced Typing Indicator
  const TypingIndicator = () => (
    <div className="flex justify-start">
      <div className="bg-gray-50 text-gray-800 rounded-2xl rounded-bl-none p-4 border border-gray-100">
        <div className="flex items-center space-x-3">
          <div className="flex space-x-1">
            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
          </div>
          <span className="text-xs text-gray-600">{currentTypingText}</span>
        </div>
      </div>
    </div>
  );

  // Enhanced Chat Header with Status
  const ChatHeader = () => (
    <div className="p-4 border-b border-gray-200/60 bg-gradient-to-r from-primary-500/5 to-secondary-500/5">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="relative">
            <div className="w-10 h-10 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full flex items-center justify-center shadow-lg">
              <span className="text-white font-semibold text-sm">EM</span>
            </div>
            <div className={`absolute -bottom-1 -right-1 w-3 h-3 rounded-full border-2 border-white ${
              isOnline ? 'bg-green-500 animate-pulse' : 'bg-gray-400'
            }`}></div>
          </div>
          <div>
            <h3 className="font-bold text-gray-900">Eng. Mathias - Solar Expert</h3>
            <p className="text-xs text-green-600 flex items-center">
              <span className="w-2 h-2 bg-green-500 rounded-full mr-1 animate-pulse"></span>
              Online • Ready to help
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-1">
          <button 
            onClick={isListening ? stopVoiceInput : startVoiceInput}
            disabled={isListening}
            className={`p-2 rounded-lg transition-colors ${
              isListening 
                ? 'bg-red-100 text-red-600' 
                : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
            }`}
            title="Voice input"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
            </svg>
          </button>
          <button 
            onClick={maximizeChat}
            className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-lg"
            title="Maximize"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5" />
            </svg>
          </button>
          <button 
            onClick={closeChat}
            className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-lg"
            title="Minimize"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );

  // Enhanced Input Area with Voice
  const ChatInput = () => (
    <div className="p-4 border-t border-gray-200/60 bg-white/50">
      {/* Quick Replies */}
      {suggestedQuestions.length > 0 && chatMessages.length > 1 && (
        <div className="mb-3">
          <div className="flex flex-wrap gap-2">
            {suggestedQuestions.slice(0, 3).map((reply, index) => (
              <button
                key={index}
                onClick={() => handleQuickReply(reply)}
                className="text-xs bg-primary-50 hover:bg-primary-100 text-primary-700 px-3 py-1.5 rounded-full transition-colors duration-200 border border-primary-200"
              >
                {reply}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex space-x-2">
        <div className="flex-1 relative">
          <textarea
            value={userInput}
            onChange={handleInputChange}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                sendMessage();
              }
            }}
            placeholder="Ask about solar tracking, pricing, installation..."
            rows="1"
            className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none pr-20 bg-white/80 backdrop-blur-sm"
            style={{ minHeight: '48px', maxHeight: '120px' }}
          />
          
          {/* Voice Input Indicator */}
          {isListening && (
            <div className="absolute right-12 bottom-3">
              <div className="w-3 h-3 bg-red-500 rounded-full animate-ping"></div>
            </div>
          )}

          <div className="absolute right-2 bottom-2 flex space-x-1">
            <button 
              onClick={isListening ? stopVoiceInput : startVoiceInput}
              className={`p-2 rounded-lg transition-colors ${
                isListening 
                  ? 'bg-red-100 text-red-600' 
                  : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
              </svg>
            </button>
            <button 
              onClick={sendMessage}
              disabled={!userInput.trim()}
              className={`p-2 rounded-lg transition-colors ${
                userInput.trim() 
                  ? 'bg-primary-600 hover:bg-primary-700 text-white' 
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            </button>
          </div>
        </div>
      </div>
      
      {/* Chat Rating */}
      {showRating && (
        <div className="mt-3 p-3 bg-gradient-to-r from-primary-50 to-secondary-50 rounded-xl border border-primary-200">
          <p className="text-xs text-gray-700 mb-2 text-center">How was your chat experience?</p>
          <div className="flex justify-center space-x-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                onClick={() => {
                  setChatRating(star);
                  setShowRating(false);
                  showSystemNotification('Thank you for your feedback!', 'We appreciate your input', 'success');
                }}
                className={`text-lg transition-transform hover:scale-110 ${
                  star <= chatRating ? 'text-yellow-500' : 'text-gray-300'
                }`}
              >
                ★
              </button>
            ))}
          </div>
        </div>
      )}

      <p className="text-xs text-gray-500 text-center mt-2">
        💡 Pro tip: Try voice input or ask about specific solar scenarios
      </p>
    </div>
  );

  // Site wellbeing initialization
  const initializeSiteWellbeing = useCallback(() => {
    // Check preferred theme
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    const savedTheme = localStorage.getItem('duaaxis-theme');
    
    if (savedTheme === 'dark' || (!savedTheme && prefersDark)) {
      document.documentElement.classList.add('dark');
    }

    // Initialize reduced motion preference
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      document.documentElement.classList.add('reduce-motion');
    }

    // Check if user has previously interacted with chat
    const hasChatHistory = localStorage.getItem('duaaxis-chat-history');
    if (hasChatHistory) {
      try {
        const history = JSON.parse(hasChatHistory);
        setChatHistory(history);
      } catch (error) {
        console.error('Error parsing chat history:', error);
        localStorage.removeItem('duaaxis-chat-history');
      }
    }

    // Get user location (simplified)
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          // In a real app, you would reverse geocode this
          setUserLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            country: 'your region'
          });
        },
        (error) => {
          console.log('Location access denied or unavailable');
        }
      );
    }
  }, []);

  // Initialize chat with welcome message when opened
  useEffect(() => {
    if (chatState !== CHAT_STATES.MINIMIZED && chatMessages.length === 0) {
      const welcomeMessage = {
        id: Date.now(),
        text: "Hello! I'm Eng. Mathias, your solar energy consultant at DuaAxis. I can help you with:\n\n• Product information & features\n• Pricing & financing options\n• Installation process & timeline\n• System benefits & efficiency\n• Maintenance & support\n\nHow can I assist you with solar tracking today? 🌞",
        sender: 'ai',
        timestamp: new Date()
      };
      setChatMessages([welcomeMessage]);
      setSuggestedQuestions(generateSuggestedQuestions([]));
    }
  }, [chatState, chatMessages.length, generateSuggestedQuestions]);

  // Auto-scroll to bottom of chat
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatMessages, isTyping]);

  // Enhanced initialization - FIXED ESLINT WARNING
  useEffect(() => {
    initializeSiteWellbeing();
    
    const timer = setTimeout(() => {
      setIsLoading(false);
      showSystemNotification('Welcome to DuaAxis!', 'Discover solar tracking excellence', 'info');
    }, 1500);

    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 500);
      
      // Enhanced section detection
      const sections = ['hero', 'stats', 'about', 'features', 'problem', 'testimonials', 'contact'];
      const scrollPosition = window.scrollY + 100;

      let currentSection = 'hero';
      
      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetBottom = offsetTop + element.offsetHeight;
          
          if (scrollPosition >= offsetTop && scrollPosition < offsetBottom) {
            currentSection = section;
            break;
          }
        }
      }
      
      setActiveSection(currentSection);
    };

    const handleOnline = () => {
      setIsOnline(true);
      showSystemNotification('You are back online', 'Connection restored', 'success');
    };

    const handleOffline = () => {
      setIsOnline(false);
      showSystemNotification('You are offline', 'Some features may be limited', 'warning');
    };

    // Add event listeners
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Initial scroll check
    handleScroll();

    // Store ref values in variables for cleanup to fix ESLint warning
    const currentMessagingTimeout = messagingTimeoutRef.current;
    const currentNotificationTimeout = notificationTimeoutRef.current;
    const currentTypingInterval = typingIntervalRef.current;
    const currentRecognition = recognitionRef.current;

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearTimeout(timer);
      
      // Use the stored variables in cleanup
      if (currentMessagingTimeout) clearTimeout(currentMessagingTimeout);
      if (currentNotificationTimeout) clearTimeout(currentNotificationTimeout);
      if (currentTypingInterval) clearTimeout(currentTypingInterval);
      if (currentRecognition) {
        currentRecognition.stop();
      }
    };
  }, [initializeSiteWellbeing]); // Removed refs from dependencies as they're stored in variables

  // Enhanced scroll functions
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offsetTop = element.offsetTop - 80; // Account for fixed header
      window.scrollTo({ 
        top: offsetTop,
        behavior: 'smooth'
      });
      setActiveSection(sectionId);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ 
      top: 0, 
      behavior: 'smooth' 
    });
    setActiveSection('hero');
  };

  // Navigation items
  const navItems = [
    { id: 'hero', label: 'Home', icon: '🏠' },
    { id: 'stats', label: 'Performance', icon: '📊' },
    { id: 'about', label: 'About', icon: '👥' },
    { id: 'features', label: 'Features', icon: '⭐' },
    { id: 'problem', label: 'Why DuaAxis', icon: '💡' },
    { id: 'testimonials', label: 'Testimonials', icon: '💬' },
    { id: 'contact', label: 'Contact', icon: '📞' }
  ];

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 flex items-center justify-center z-50">
        <div className="text-center">
          <div className="relative">
            <div className="w-20 h-20 border-4 border-white/20 border-t-white rounded-full animate-spin mx-auto mb-4"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-12 h-12 bg-gradient-to-r from-secondary-400 to-secondary-600 rounded-full animate-pulse"></div>
            </div>
          </div>
          <div className="text-white text-xl font-bold mb-2 animate-pulse">DuaAxis</div>
          <div className="text-white/80 text-sm">Solar Tracking Excellence</div>
          <div className="mt-4 text-white/60 text-xs">Loading your sustainable future...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-16 relative min-h-screen bg-gradient-to-br from-white via-gray-50 to-primary-50/30">
      {/* Floating Navigation */}
      <div className="fixed right-6 top-1/2 transform -translate-y-1/2 z-40 hidden lg:block">
        <div className="bg-white/90 backdrop-blur-xl rounded-2xl shadow-2xl p-3 border border-white/20">
          <div className="space-y-3">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className={`group relative flex items-center justify-center w-4 h-4 rounded-full transition-all duration-300 ${
                  activeSection === item.id
                    ? 'bg-gradient-to-r from-primary-500 to-secondary-500 scale-125 shadow-lg'
                    : 'bg-gray-300/80 hover:bg-primary-300/80 hover:scale-110'
                }`}
                aria-label={`Scroll to ${item.label}`}
                title={item.label}
              >
                <span className="absolute -left-8 opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap text-xs font-medium text-gray-700 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-lg shadow-lg">
                  {item.icon} {item.label}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Enhanced Progress Bar */}
      <div className="fixed top-16 left-0 w-full h-1 bg-gray-200/50 z-40 backdrop-blur-sm">
        <div 
          className="h-full bg-gradient-to-r from-primary-500 via-secondary-500 to-accent-500 transition-all duration-500 ease-out shadow-lg"
          style={{ 
            width: `${(navItems.findIndex(item => item.id === activeSection) + 1) / navItems.length * 100}%`
          }}
        />
      </div>

      {/* Enhanced Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-24 right-6 bg-gradient-to-br from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 text-white p-4 rounded-2xl shadow-2xl transition-all duration-300 transform hover:scale-110 hover:shadow-2xl z-40 group"
          aria-label="Scroll to top"
        >
          <svg className="w-5 h-5 transform group-hover:-translate-y-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
          </svg>
          <div className="absolute inset-0 rounded-2xl bg-white/20 animate-ping opacity-0 group-hover:opacity-100 transition-opacity duration-1000"></div>
        </button>
      )}

      {/* Enhanced Quick Contact Floating Button */}
      <div className="fixed bottom-6 left-6 z-40 hidden lg:block">
        <div className="bg-white/90 backdrop-blur-xl rounded-2xl shadow-2xl p-4 border border-white/20 transform transition-all duration-300 hover:scale-105 hover:shadow-2xl">
          <div className="text-center">
            <div className="w-14 h-14 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-lg">
              <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
            </div>
            <div className="text-sm font-bold text-gray-900 mb-1">Need Help?</div>
            <div className="text-xs text-gray-600 mb-3">Solar Experts Available</div>
            <div className="space-y-1">
              <a
                href="tel:+20212345678"
                className="text-primary-600 hover:text-primary-700 text-sm font-semibold block transition-colors"
              >
                +20 2 1234 5678
              </a>
              <a
                href="mailto:info@duaaxis.com"
                className="text-secondary-600 hover:text-secondary-700 text-xs block transition-colors"
              >
                info@duaaxis.com
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Live Chat Widget - Minimized State */}
      {chatState === CHAT_STATES.MINIMIZED && (
        <div className="fixed bottom-6 right-6 z-50">
          <div className="relative">
            <button 
              onClick={toggleChat}
              className="bg-gradient-to-br from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white p-4 rounded-2xl shadow-2xl transition-all duration-300 transform hover:scale-110 hover:shadow-2xl group"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              {unreadMessages > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center animate-pulse">
                  {unreadMessages}
                </span>
              )}
              <div className="absolute inset-0 rounded-2xl bg-white/20 animate-ping opacity-0 group-hover:opacity-100 transition-opacity duration-1000"></div>
            </button>
            
            <div className="absolute -top-1 -left-1">
              <div className={`w-3 h-3 rounded-full border-2 border-white ${isOnline ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`}></div>
            </div>
          </div>
        </div>
      )}

      {/* Live Chat Interface - Open State */}
      {chatState === CHAT_STATES.OPEN && (
        <div className="fixed bottom-24 right-6 w-80 sm:w-96 bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/20 z-50 animate-in slide-in-from-bottom duration-500">
          <ChatHeader />
          
          {/* Chat Messages */}
          <div 
            ref={chatContainerRef}
            className="p-4 max-h-80 overflow-y-auto space-y-4 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent"
          >
            {chatMessages.map((message) => (
              <Message key={message.id} message={message} />
            ))}
            
            {isTyping && <TypingIndicator />}
          </div>

          <ChatInput />
        </div>
      )}

      {/* Live Chat Interface - Maximized State */}
      {chatState === CHAT_STATES.MAXIMIZED && (
        <div className="fixed inset-4 sm:inset-10 lg:inset-20 bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/20 z-50 animate-in zoom-in duration-500">
          <div className="flex flex-col h-full">
            <div className="p-6 border-b border-gray-200/60 bg-gradient-to-r from-primary-500/5 to-secondary-500/5">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="relative">
                    <div className="w-14 h-14 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full flex items-center justify-center shadow-xl">
                      <span className="text-white font-semibold text-lg">EM</span>
                    </div>
                    <div className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white ${
                      isOnline ? 'bg-green-500 animate-pulse' : 'bg-gray-400'
                    }`}></div>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 text-xl">Eng. Mathias - Solar Energy Consultant</h3>
                    <p className="text-sm text-green-600 flex items-center">
                      <span className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></span>
                      Online • Ready to help with all your solar questions
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <button 
                    onClick={minimizeChat}
                    className="text-gray-400 hover:text-gray-600 transition-colors p-3 hover:bg-gray-100 rounded-xl"
                    title="Restore"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5" />
                    </svg>
                  </button>
                  <button 
                    onClick={closeChat}
                    className="text-gray-400 hover:text-gray-600 transition-colors p-3 hover:bg-gray-100 rounded-xl"
                    title="Close"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            {/* Enhanced Chat Area */}
            <div 
              ref={chatContainerRef}
              className="flex-1 p-6 overflow-y-auto space-y-6 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent bg-gray-50/50"
            >
              {chatMessages.map((message) => (
                <Message key={message.id} message={message} />
              ))}
              
              {isTyping && <TypingIndicator />}
            </div>

            <ChatInput />
          </div>
        </div>
      )}

      {/* System Notifications */}
      {systemNotification && (
        <div className={`fixed top-24 right-6 max-w-sm bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border-l-4 ${
          systemNotification.type === 'success' ? 'border-green-500' :
          systemNotification.type === 'warning' ? 'border-yellow-500' :
          systemNotification.type === 'error' ? 'border-red-500' : 'border-primary-500'
        } z-50 animate-in slide-in-from-right duration-500`}>
          <div className="p-4">
            <div className="flex items-start space-x-3">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                systemNotification.type === 'success' ? 'bg-green-100 text-green-600' :
                systemNotification.type === 'warning' ? 'bg-yellow-100 text-yellow-600' :
                systemNotification.type === 'error' ? 'bg-red-100 text-red-600' : 'bg-primary-100 text-primary-600'
              }`}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {systemNotification.type === 'success' ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  )}
                </svg>
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900 text-sm">{systemNotification.title}</h4>
                <p className="text-gray-600 text-sm mt-1">{systemNotification.message}</p>
              </div>
              <button 
                onClick={() => setSystemNotification(null)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Network Status Indicator */}
      {!isOnline && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 bg-yellow-500 text-white px-4 py-2 rounded-lg shadow-lg z-50 animate-bounce">
          <div className="flex items-center space-x-2 text-sm">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <span>You are currently offline</span>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="relative">
        <section id="hero">
          <Hero />
        </section>
        
        <section id="stats">
          <Stats />
        </section>
        
        <section id="about">
          <About />
        </section>
        
        <section id="features">
          <Features />
        </section>
        
        <section id="problem">
          <ProblemSolution />
        </section>
        
        <section id="testimonials">
          <Testimonials />
        </section>
        
        <section id="contact">
          <Contact />
        </section>
      </div>

      <style jsx>{`
        @keyframes gradientShift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        
        .reduce-motion * {
          animation-duration: 0.01ms !important;
          animation-iteration-count: 1 !important;
          transition-duration: 0.01ms !important;
        }

        .scrollbar-thin::-webkit-scrollbar {
          width: 4px;
        }

        .scrollbar-thumb-gray-300::-webkit-scrollbar-thumb {
          background-color: #d1d5db;
          border-radius: 2px;
        }

        .scrollbar-track-transparent::-webkit-scrollbar-track {
          background: transparent;
        }
      `}</style>
    </div>
  );
};

export default Home;